<template>
  <div class="biz-dashboard">

    <!-- ============ 顶部：统计周期 + 刷新 ============ -->
    <div class="page-header">
      <div class="header-left">
        <h2>运营看板</h2>
        <span class="sub-title">生意数据一览 · 访问日志见下方「运维视图」</span>
      </div>
      <div class="header-right">
        <el-radio-group v-model="days" size="small" @change="loadAll">
          <el-radio-button :value="7">近 7 天</el-radio-button>
          <el-radio-button :value="30">近 30 天</el-radio-button>
          <el-radio-button :value="90">近 90 天</el-radio-button>
        </el-radio-group>
        <el-button size="small" :icon="Refresh" :loading="loading" @click="loadAll">刷新</el-button>
        <span class="last-update" v-if="lastUpdate">更新于 {{ lastUpdate }}</span>
      </div>
    </div>

    <!-- ============ KPI 卡片 ============ -->
    <el-row :gutter="12" v-loading="loading">
      <el-col v-for="c in cards" :key="c.key" :xs="12" :sm="8" :md="4">
        <div class="stat-card">
          <div class="stat-head">
            <span class="stat-label">{{ c.label }}</span>
            <el-tooltip v-if="c.tip" :content="c.tip" placement="top">
              <el-icon class="tip-icon"><InfoFilled /></el-icon>
            </el-tooltip>
          </div>
          <div class="stat-value">{{ format(cardsValue[c.key]) }}<span class="unit">{{ c.unit }}</span></div>
          <div class="stat-foot muted">{{ c.period }}</div>
        </div>
      </el-col>
    </el-row>

    <!-- ============ 待办事项 ============ -->
    <el-card class="todo-card" shadow="never">
      <template #header>
        <div class="card-head">
          <span>待办事项</span>
          <el-badge v-if="todoTotal > 0" :value="todoTotal" type="danger" class="todo-badge" />
        </div>
      </template>

      <el-empty v-if="todoTotal === 0" description="一切正常，没有待处理事项" :image-size="60" />

      <div v-else class="todo-list">
        <div class="todo-item" v-if="todos.staleOrders > 0">
          <el-icon class="todo-icon warn"><Warning /></el-icon>
          <span>有 <b>{{ todos.staleOrders }}</b> 笔超期 24 小时未支付也未关闭的订单（关单任务可能异常）</span>
        </div>
        <div class="todo-item" v-if="(todos.paidNoEntitlement || []).length > 0">
          <el-icon class="todo-icon danger"><CircleCloseFilled /></el-icon>
          <span>
            有 <b>{{ todos.paidNoEntitlement.length }}</b> 笔订单<b>已支付但权益未发放</b>（用户付费后无权益，需人工补发）
            <el-link type="primary" class="ml8" @click="$router.push('/admin/home/orders')">去处理</el-link>
          </span>
        </div>
        <div class="todo-item" v-if="(todos.missingPrice || []).length > 0">
          <el-icon class="todo-icon warn"><Warning /></el-icon>
          <span>
            有 <b>{{ todos.missingPrice.length }}</b> 个上架产品定价缺失或为 0（用户下单会报错）
            <el-link type="primary" class="ml8" @click="$router.push('/admin/home/plugins')">去设置</el-link>
          </span>
        </div>
        <div class="todo-item" v-if="todos.expiringSoon > 0">
          <el-icon class="todo-icon info"><InfoFilled /></el-icon>
          <span>有 <b>{{ todos.expiringSoon }}</b> 份权益将在 7 天内到期（可做续费引导）</span>
        </div>
      </div>
    </el-card>

    <!-- ============ 图表区 ============ -->
    <el-row :gutter="12">
      <el-col :xs="24" :lg="16">
        <el-card shadow="never" class="chart-card">
          <template #header>
            <div class="card-head">
              <span>营收趋势（近 {{ days }} 天）</span>
              <el-radio-group v-model="chartMode" size="small">
                <el-radio-button value="both">金额+订单</el-radio-button>
                <el-radio-button value="revenue">仅金额</el-radio-button>
              </el-radio-group>
            </div>
          </template>
          <div ref="revenueRef" class="chart-box"></div>
        </el-card>
      </el-col>

      <el-col :xs="24" :lg="8">
        <el-card shadow="never" class="chart-card">
          <template #header><span>收入构成（本月）</span></template>
          <div ref="pieRef" class="chart-box"></div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="12">
      <el-col :xs="24" :lg="12">
        <el-card shadow="never" class="chart-card">
          <template #header><span>产品收入 Top 10（本月）</span></template>
          <div ref="topRef" class="chart-box"></div>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="12">
        <el-card shadow="never" class="chart-card">
          <template #header><span>档位分布（本月）</span></template>
          <div ref="tierRef" class="chart-box"></div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="12">
      <el-col :xs="24" :lg="12">
        <el-card shadow="never" class="chart-card">
          <template #header><span>用户活跃（DAU / 登录 / exe 启动）</span></template>
          <div ref="activeRef" class="chart-box"></div>
        </el-card>
      </el-col>

      <!-- 实时动态 -->
      <el-col :xs="24" :lg="12">
        <el-card shadow="never" class="chart-card">
          <template #header>
            <div class="card-head">
              <span>实时动态</span>
              <el-switch v-model="autoRefresh" size="small" active-text="30 秒自动刷新" />
            </div>
          </template>
          <div class="activity-list">
            <el-empty v-if="activity.length === 0" description="暂无订单动态" :image-size="60" />
            <div v-for="a in activity" :key="a.id" class="activity-item">
              <el-tag :type="statusTag(a.payment_status)" size="small" class="act-tag">
                {{ statusText(a.payment_status) }}
              </el-tag>
              <span class="act-text">
                <b>{{ a.buyer_nickname || ('用户' + (a.buyer_id || '-')) }}</b>
                {{ statusText(a.payment_status, true) }}
                <b>{{ a.product_name || a.product_code }}</b>
                <span v-if="a.purchased_days" class="muted">（{{ a.purchased_days }} 天）</span>
              </span>
              <span class="act-time">{{ fmtTime(a.paid_at || a.created_at) }}</span>
              <span class="act-amount" v-if="a.payment_status === 1 && a.total_fee">¥{{ Number(a.total_fee).toFixed(2) }}</span>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import * as echarts from 'echarts'
import request from '@/utils/request'
import { Refresh, InfoFilled, Warning, CircleCloseFilled } from '@element-plus/icons-vue'

const days = ref(7)
const loading = ref(false)
const lastUpdate = ref('')
const chartMode = ref('both')
const autoRefresh = ref(true)

const overview = ref({})
const todos = ref({})
const activity = ref([])
const revenueSeries = ref([])
const byLine = ref([])
const byProduct = ref([])
const byDays = ref([])
const activeSeries = ref([])
const exeSeries = ref([])

const revenueRef = ref(null)
const pieRef = ref(null)
const topRef = ref(null)
const tierRef = ref(null)
const activeRef = ref(null)

let charts = []
let timer = null

const cards = [
  { key: 'today_orders', label: '今日订单', unit: ' 笔', period: '今天已支付的订单数', tip: '今天已支付的订单数' },
  { key: 'today_revenue', label: '今日营收', unit: ' 元', period: '今天已支付的金额', tip: '今天已支付的订单金额合计' },
  { key: 'today_buyers', label: '付费人数', unit: ' 人', period: '今天有付款的去重用户', tip: '今天有付款行为的去重用户数' },
  { key: 'today_active_users', label: '今日活跃', unit: ' 人', period: '今天有登录记录的去重用户', tip: '今天有登录记录的去重用户数' },
  { key: 'month_revenue', label: '本月营收', unit: ' 元', period: '本月累计金额', tip: '本月累计订单金额' },
  { key: 'active_entitlements', label: '有效权益', unit: ' 份', period: '当前未过期的授权数', tip: '当前未过期的授权数量' },
]

const cardsValue = computed(() => ({
  today_orders: overview.value.today_orders,
  today_revenue: overview.value.today_revenue,
  today_buyers: overview.value.today_buyers,
  today_active_users: overview.value.today_active_users,
  month_revenue: overview.value.month_revenue,
  active_entitlements: overview.value.active_entitlements,
}))

const todoTotal = computed(() => {
  const t = todos.value || {}
  return (+(t.staleOrders || 0))
    + ((t.paidNoEntitlement || []).length)
    + ((t.missingPrice || []).length)
    + (+(t.expiringSoon || 0))
})

const format = (v) => {
  if (v === null || v === undefined || v === '') return '0'
  const n = Number(v)
  if (Number.isNaN(n)) return String(v)
  return n.toLocaleString('zh-CN', { maximumFractionDigits: 2 })
}

const statusText = (s, verb = false) => ({
  0: verb ? '下单了' : '待支付',
  1: verb ? '支付了' : '已支付',
  2: verb ? '退款了' : '已退款',
  3: verb ? '关闭了' : '已关闭',
}[s] || '未知')
const statusTag = (s) => ({ 0: 'info', 1: 'success', 2: 'warning', 3: 'danger' }[s] || 'info')

const fmtTime = (t) => {
  if (!t) return '-'
  const d = new Date(t)
  const diff = (Date.now() - d.getTime()) / 1000
  if (diff < 60) return '刚刚'
  if (diff < 3600) return `${Math.floor(diff / 60)} 分钟前`
  if (diff < 86400) return `${Math.floor(diff / 3600)} 小时前`
  const pad = (n) => String(n).padStart(2, '0')
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const loadAll = async () => {
  loading.value = true
  try {
    const [ov, daily, breakdown, act, todoData, actData] = await Promise.all([
      request.get('/admin/monitor/overview'),
      request.get(`/admin/monitor/revenue/daily?days=${days.value}`),
      request.get('/admin/monitor/revenue/breakdown'),
      request.get(`/admin/monitor/users/active?days=${days.value}`),
      request.get('/admin/monitor/todos'),
      request.get('/admin/monitor/recent?limit=10'),
    ])
    if (ov.code === '200') overview.value = ov.data || {}
    if (todoData.code === '200') todos.value = todoData.data || {}
    if (actData.code === '200') activity.value = actData.data || []
    if (daily.code === '200') revenueSeries.value = daily.data || []
    if (breakdown.code === '200') {
      byLine.value = breakdown.data?.byLine || []
      byProduct.value = breakdown.data?.byProduct || []
      byDays.value = breakdown.data?.byDays || []
    }
    if (act.code === '200') {
      activeSeries.value = act.data?.daily || []
      exeSeries.value = act.data?.exeUsage || []
    }
    lastUpdate.value = new Date().toLocaleTimeString('zh-CN', { hour12: false })
    await nextTick()
    renderCharts()
  } catch (e) {
    console.error('加载看板数据失败:', e)
  } finally {
    loading.value = false
  }
}

const AXIS = { color: '#909399' }
const GRID = { left: 56, right: 24, top: 40, bottom: 32 }

const renderCharts = () => {
  charts.forEach(c => c.dispose())
  charts = []

  if (revenueRef.value) {
    const c = echarts.init(revenueRef.value)
    const isBoth = chartMode.value === 'both'
    c.setOption({
      tooltip: { trigger: 'axis' },
      legend: isBoth ? { data: ['营收', '订单数'] } : undefined,
      grid: GRID,
      xAxis: { type: 'category', data: revenueSeries.value.map(r => String(r.day).slice(5)), axisLabel: AXIS },
      yAxis: isBoth
        ? [{ type: 'value', name: '营收(元)', splitLine: { lineStyle: { type: 'dashed' } } },
           { type: 'value', name: '订单数', splitLine: { show: false } }]
        : [{ type: 'value', name: '营收(元)', splitLine: { lineStyle: { type: 'dashed' } } }],
      series: isBoth
        ? [
            { name: '营收', type: 'bar', data: revenueSeries.value.map(r => +r.revenue || 0),
              itemStyle: { color: '#409EFF', borderRadius: [4, 4, 0, 0] }, barMaxWidth: 26 },
            { name: '订单数', type: 'line', yAxisIndex: 1, smooth: true,
              data: revenueSeries.value.map(r => +r.orders || 0), itemStyle: { color: '#E6A23C' } },
          ]
        : [{ name: '营收', type: 'line', smooth: true,
            data: revenueSeries.value.map(r => +r.revenue || 0),
            areaStyle: { color: 'rgba(64,158,255,0.15)' }, itemStyle: { color: '#409EFF' } }],
    })
    charts.push(c)
  }

  if (pieRef.value) {
    const c = echarts.init(pieRef.value)
    c.setOption({
      tooltip: { trigger: 'item', formatter: (p) => `${p.name}: ¥${p.value} (${p.percent}%)` },
      legend: { bottom: 0 },
      series: [{
        type: 'pie',
        radius: ['42%', '68%'],
        center: ['50%', '45%'],
        data: byLine.value.map(r => ({
          name: r.product_line === 'aip' ? 'AI 插件 (aip)' : 'exe 程序插件',
          value: +r.revenue || 0,
        })),
        label: { formatter: '{b}\n¥{c}' },
      }],
    })
    charts.push(c)
  }

  if (topRef.value) {
    const c = echarts.init(topRef.value)
    const rows = byProduct.value.slice(0, 10).reverse()
    c.setOption({
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      grid: { left: 120, right: 60, top: 20, bottom: 30 },
      xAxis: { type: 'value', splitLine: { lineStyle: { type: 'dashed' } } },
      yAxis: { type: 'category', data: rows.map(r => r.product_name || r.product_code), axisLabel: AXIS },
      series: [{
        type: 'bar',
        data: rows.map(r => +r.revenue || 0),
        itemStyle: { color: '#67C23A', borderRadius: [0, 4, 4, 0] },
        barMaxWidth: 18,
        label: { show: true, position: 'right', formatter: (p) => '¥' + p.value },
      }],
    })
    charts.push(c)
  }

  if (tierRef.value) {
    const c = echarts.init(tierRef.value)
    c.setOption({
      tooltip: { trigger: 'axis' },
      grid: GRID,
      xAxis: { type: 'category', data: byDays.value.map(r => r.days + ' 天'), axisLabel: AXIS },
      yAxis: { type: 'value', name: '营收(元)', splitLine: { lineStyle: { type: 'dashed' } } },
      series: [{ type: 'bar', data: byDays.value.map(r => +r.revenue || 0),
                 itemStyle: { color: '#909399', borderRadius: [4, 4, 0, 0] }, barMaxWidth: 40 }],
    })
    charts.push(c)
  }

  if (activeRef.value) {
    const c = echarts.init(activeRef.value)
    c.setOption({
      tooltip: { trigger: 'axis' },
      legend: { data: ['活跃用户', '登录次数', 'exe 启动'] },
      grid: GRID,
      xAxis: { type: 'category', data: activeSeries.value.map(r => String(r.day).slice(5)), axisLabel: AXIS },
      yAxis: { type: 'value', splitLine: { lineStyle: { type: 'dashed' } } },
      series: [
        { name: '活跃用户', type: 'line', smooth: true, data: activeSeries.value.map(r => +r.dau || 0), itemStyle: { color: '#409EFF' } },
        { name: '登录次数', type: 'line', smooth: true, data: activeSeries.value.map(r => +r.logins || 0), itemStyle: { color: '#E6A23C' } },
        { name: 'exe 启动', type: 'line', smooth: true, data: exeSeries.value.map(r => +r.starts || 0), itemStyle: { color: '#C0C4CC' } },
      ],
    })
    charts.push(c)
  }
}

watch(chartMode, () => renderCharts())

const handleResize = () => charts.forEach(c => c.resize())

const setupTimer = () => {
  if (timer) clearInterval(timer)
  timer = null
  if (autoRefresh.value) {
    // 只轮询轻量的"实时动态"，不重拉全部图表
    timer = setInterval(async () => {
      try {
        const res = await request.get('/admin/monitor/recent?limit=10')
        if (res.code === '200') activity.value = res.data || []
      } catch (e) { /* 静默 */ }
    }, 30000)
  }
}
watch(autoRefresh, setupTimer)

onMounted(() => {
  loadAll()
  setupTimer()
  window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
  if (timer) clearInterval(timer)
  charts.forEach(c => c.dispose())
  charts = []
})
</script>

<style scoped>
.biz-dashboard { padding: 14px; background: #f5f7fa; min-height: 100%; }
.page-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; flex-wrap: wrap; gap: 10px; }
.header-left h2 { margin: 0; font-size: 20px; }
.sub-title { font-size: 12px; color: #909399; }
.header-right { display: flex; align-items: center; gap: 10px; }
.last-update { font-size: 12px; color: #c0c4cc; }
.stat-card { background: #fff; border-radius: 8px; padding: 14px; margin-bottom: 12px; border: 1px solid #ebeef5; transition: box-shadow .2s; }
.stat-card:hover { box-shadow: 0 2px 12px rgba(0,0,0,.06); }
.stat-head { display: flex; align-items: center; gap: 4px; }
.stat-label { font-size: 13px; color: #909399; }
.tip-icon { font-size: 13px; color: #c0c4cc; cursor: help; }
.stat-value { font-size: 26px; font-weight: 600; color: #303133; line-height: 1.3; margin-top: 4px; }
.stat-value .unit { font-size: 13px; font-weight: normal; color: #909399; margin-left: 2px; }
.stat-foot { font-size: 12px; margin-top: 4px; }
.muted { color: #c0c4cc; }
.todo-card, .chart-card { margin-bottom: 12px; }
.card-head { display: flex; align-items: center; justify-content: space-between; }
.todo-badge { margin-left: 8px; }
.todo-list { display: flex; flex-direction: column; gap: 8px; }
.todo-item { display: flex; align-items: center; gap: 8px; padding: 9px 12px; background: #fafafa; border-radius: 6px; font-size: 13px; }
.todo-icon { font-size: 16px; }
.todo-icon.danger { color: #dc2626; }
.todo-icon.warn { color: #e6a23c; }
.todo-icon.info { color: #909399; }
.ml8 { margin-left: 8px; }
.chart-box { height: 320px; width: 100%; }
.activity-list { max-height: 320px; overflow-y: auto; }
.activity-item { display: flex; align-items: center; gap: 8px; padding: 7px 0; border-bottom: 1px solid #f2f2f2; font-size: 13px; }
.activity-item:last-child { border-bottom: none; }
.act-tag { flex-shrink: 0; }
.act-text { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.act-time { color: #c0c4cc; font-size: 12px; flex-shrink: 0; }
.act-amount { color: #dc2626; font-size: 12px; flex-shrink: 0; font-weight: 600; }
</style>